// ImportReview.tsx (S26) - paste a PokerStars/GG/PHH hand, POST it to the S16 importer, and review the
// parsed HandRecord before replaying or saving it. A malformed or illegal history shows the parser's
// exact reason inline (red). "Replay this hand" scrubs it in the timeline; "Save to my hands" persists
// it via the S14 /api/hands route. This is the only surface that wires to /api/import + persistence.
import { useMemo, useState } from 'react';
import { Card } from '../components/Card';
import { segmentHand } from './segment';
import { ReplayTimeline } from './ReplayTimeline';
import { useImport } from './useImport';

export function ImportReview() {
  const [text, setText] = useState('');
  const [replaying, setReplaying] = useState(false);
  const { state, run, save, reset } = useImport();

  const segments = useMemo(() => (state.record ? segmentHand(state.record) : null), [state.record]);

  if (replaying && segments) {
    return (
      <div className="importview">
        <button type="button" className="rp-back" onClick={() => setReplaying(false)}>
          {'←'} Back to review
        </button>
        <ReplayTimeline segments={segments} sourceLabel="Imported" />
      </div>
    );
  }

  return (
    <div className="importview">
      <textarea
        className="paste"
        placeholder="Paste a PokerStars or GG hand history, or a PHH/TOML hand."
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          if (state.record || state.error) reset();
        }}
        spellCheck={false}
      />
      <div className="importbar">
        <button type="button" className="rp-importbtn" disabled={!text.trim() || state.loading} onClick={() => run(text)}>
          {state.loading ? 'Importing…' : 'Import hand'}
        </button>
        <div className="fmt">
          Detects <b>PokerStars</b>, GG text, or PHH/TOML
        </div>
      </div>

      {state.error && (
        <div className="err">
          <span className="x">!</span>
          <div>{state.error}</div>
        </div>
      )}

      {segments && (
        <>
          <div className="reviewhdr">
            <span className="chip">Imported</span>
            <div className="meta">
              <b>{segments.stakesLabel}</b> {segments.tableSize}-max &middot; Hero{' '}
              {segments.heroPosition} &middot;{' '}
              {segments.result.heroWon ? (
                <>
                  won <b>${segments.result.potWon}</b>
                </>
              ) : (
                <>lost the pot</>
              )}
            </div>
          </div>

          <div className="rp-board rp-final">
            <div className="rp-rail">
              <span className="rp-street">Final board</span>
              <span className="rp-pot num">
                Pot <b>${segments.result.potWon}</b>
              </span>
            </div>
            <div className="rp-commrow">
              <div className="rp-comm">
                {segments.finalBoard.map((card, i) => (
                  <Card key={i} card={card} />
                ))}
              </div>
              <div className="rp-herochip">
                <div className="rp-lb">Hero</div>
                <div className="rp-cards">
                  <Card card={segments.heroCards[0]} />
                  <Card card={segments.heroCards[1]} />
                </div>
              </div>
            </div>
          </div>

          <div className="rp-dock">
            <button type="button" className="primary" onClick={() => setReplaying(true)}>
              Replay this hand
            </button>
            <button
              type="button"
              disabled={state.saving || !!state.savedId}
              onClick={() => state.record && save(state.record, Date.now())}
            >
              {state.savedId ? 'Saved ✓' : state.saving ? 'Saving…' : 'Save to my hands'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
